import axios from 'axios';
import _ from 'lodash';
import {
  EHAND_API_URL,
  EQUIPMENT_API_URL,
  SYS_API_URL,
  BS_API_URL,
  DUTY_API_URL,
  EADMISSION_API_URL,
  TIMEOUT,
} from '../comm/config';
import { LOGIN_INFO } from '../comm/constant';
import { i18n } from '../language';
import { store } from '../redux';
import { LOADING_HIDE, LOADING_SHOW } from '../redux/constant';
import { getAuthToken, getEhandoverHeaderParams } from '../utils';
import { checkToken, tokenInvalid, userInActive } from '../utils/check';
import throttleUrlMap from '../comm/throttleUrlMap';
import { alertMsg, ResponseCommCodeEnum } from './commlogic';
import errorCodeMapping from './teleVisit/errorCodeMapping';
import { showLoading, hideLoading } from './loadingGuard';

const AxiosRequestConfig = {
  timeout: TIMEOUT,
};

const EHANDOVER_NETWORK_ERROR_ALERT_WINDOW = 30 * 1000;
const EHANDOVER_NETWORK_ERROR_ALERT_THRESHOLD = 10;
const maxRetries = 3;

const instance = axios.create(AxiosRequestConfig);

instance.defaults.headers.post['Content-Type'] = 'text/plain; charset=utf-8;';

const throttleMap = new Map();

const EHANDOVER_API_HOST = EHAND_API_URL;
const EQUIPMENT_API_HOST = EQUIPMENT_API_URL;
const SYSTEM_API_HOST = SYS_API_URL;
const BED_STATUS_API_HOST = BS_API_URL;
const DUTY_API_HOST = DUTY_API_URL;
const EADMISSION_API_HOST = EADMISSION_API_URL;

// 这个方法处理了ehandover模块export以外其他api的请求超时的network error
// 只有在30秒内连续出现10次504才会出network error的alert
const ehandoverNetworkErrorControlClass = () => {
  let count = 0;
  let firstErrorTime = null;
  let timer = null;
  let hasAlerted = false;

  const reset = () => {
    count = 0;
    firstErrorTime = null;
    hasAlerted = false;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const startWindow = () => {
    firstErrorTime = Date.now();
    timer = setTimeout(() => {
      reset();
    }, EHANDOVER_NETWORK_ERROR_ALERT_WINDOW);
  };

  const isEhandoverTimeoutError = (error) => {
    const status = error?.response?.status;
    const message = error?.message?.toLowerCase?.() || '';
    return status === 504 || error?.code === 'ECONNABORTED' || message.includes('timeout');
  };

  const shouldHandle = (error) => {
    return error?.config?.url?.startsWith(EHANDOVER_API_HOST) && isEhandoverTimeoutError(error);
  };

  // EHandover 的网络超时在 30 秒窗口内累计到阈值时才弹一次提示。
  const record = (error) => {
    if (!shouldHandle(error)) {
      return false;
    }

    const now = Date.now();
    if (!firstErrorTime || now - firstErrorTime >= EHANDOVER_NETWORK_ERROR_ALERT_WINDOW) {
      reset();
      startWindow();
    }

    count += 1;
    if (count >= EHANDOVER_NETWORK_ERROR_ALERT_THRESHOLD && !hasAlerted) {
      hasAlerted = true;
      setTimeout(() => {
        alertMsg(i18n.t('ALERT_NETWORK_ERROR'), _.uniqueId('error_'));
      }, 300);
    }

    return true;
  };

  return {
    record,
  };
};

const ehandoverNetworkErrorControl = ehandoverNetworkErrorControlClass();

const getAuthorization = (shouldSkipAuth = false) => {
  const Authorization = getAuthToken();
  if (!shouldSkipAuth && Authorization) {
    return { Authorization };
  } else {
    return {};
  }
};

const checkTokenFn = async (response) => {
  const invalidTokenHandling = () => {
    const currentState = store.getState();
    const expiryLoginState = currentState.expiryLogin;
    if (!expiryLoginState.isOpen) {
      tokenInvalid(false);
      alertMsg(
        i18n.t('ALERT_IDLE_DETECTED_YOU_ARE_SIGNED_OUT_BECAUSE_OF_SECURITY_CONCERN'),
        _.uniqueId('warning_'),
        'warning'
      );
    }
  };

  const result = await checkToken(true, true);
  if (result) {
    try {
      const login = localStorage.getItem(LOGIN_INFO);
      const { token } = JSON.parse(login);
      const oldHeaders = response.config.headers;
      const newHeaders = { ...oldHeaders, Authorization: token ?? '' };
      return { success: true, response: await instance({ ...response.config, headers: newHeaders }) };
    } catch (error) {
      if (ResponseCommCodeEnum.USER_INACTIVE === (error?.statusCode ?? '')) {
        invalidTokenHandling();
      }
      return { success: false, response: error };
    }
  } else {
    invalidTokenHandling();
    return { success: false };
  }
};

function readBlob(blob) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsText(blob);
  });
}

const getThrottledRequest = (url, shouldSkipLoading) => {
  if (!throttleMap.has(url)) {
    const throttled = _.throttle((resolve, originalConfig) => {
      if (!shouldSkipLoading) {
        const reqId = showLoading(originalConfig.url);
        originalConfig._requestId = reqId;
      }
      if (originalConfig.headers?.isHideLoading) {
        delete originalConfig.headers.isHideLoading;
      }
      resolve(originalConfig);
    }, TIMEOUT);
    throttleMap.set(url, throttled);
  }
  return throttleMap.get(url);
};

instance.interceptors.request.use(
  (config) => {
    // Preserve the skip flag internally before deletion
    const shouldSkipLoading = Boolean(config.headers?.isHideLoading);
    config._internalSkipLoading = shouldSkipLoading;

    if (config.headers?.isHideLoading) {
      delete config.headers.isHideLoading;
    }

    config._isHideError = Boolean(config.headers?.isHideError);

    if (config.headers?.isHideError) {
      delete config.headers.isHideError;
    }

    config._retryCount = config._retryCount || 0;

    let logHeaders = {};
    if (config.url.startsWith(EHANDOVER_API_HOST) && config.url.indexOf('/task/export') === -1) {
      logHeaders = getEhandoverHeaderParams(config.url, EHANDOVER_API_HOST);
    } else if (config.url.startsWith(EQUIPMENT_API_HOST)) {
      logHeaders = getEhandoverHeaderParams(config.url, EQUIPMENT_API_HOST);
    } else if (config.url.startsWith(SYSTEM_API_HOST)) {
      logHeaders = getEhandoverHeaderParams(config.url, SYSTEM_API_HOST);
    } else if (config.url.startsWith(BED_STATUS_API_HOST)) {
      logHeaders = getEhandoverHeaderParams(config.url, BED_STATUS_API_HOST);
    } else if (config.url.startsWith(DUTY_API_HOST)) {
      logHeaders = getEhandoverHeaderParams(config.url, DUTY_API_HOST);
    } else if (config.url.startsWith(EADMISSION_API_HOST)) {
      logHeaders = getEhandoverHeaderParams(config.url, EADMISSION_API_HOST);
    }

    const { withoutToken, ...restHeaders } = config.headers || {};
    config.headers = {
      ...restHeaders,
      ...getAuthorization(withoutToken ?? false),
      ...logHeaders,
    };

    if (_.some(throttleUrlMap, (item) => config.url.includes(item))) {
      const throttleRequest = getThrottledRequest(config.url, shouldSkipLoading);
      return new Promise((resolve) => {
        throttleRequest(resolve, config);
      });
    } else {
      // Non-throttled: Use the imported guard
      if (!shouldSkipLoading) {
        const reqId = showLoading(config.url);
        config._requestId = reqId;
      }
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);
const errorMessageFun = (statusCode) => {
  let text;
  Object.keys(errorCodeMapping.errorMessage).forEach((key) => {
    if (key === statusCode) {
      text = errorCodeMapping.errorMessage[key];
    }
  });
  return alertMsg(i18n.t(text), _.uniqueId('error_'));
};

instance.interceptors.response.use(
  async (response) => {
    const { url, _requestId: reqId, _internalSkipLoading: shouldSkip, _isHideError: shouldHideError } = response.config;

    // Use imported guard
    if (!shouldSkip && reqId) {
      hideLoading(url, reqId, false);
    } else if (!shouldSkip && url) {
      // Fallback
      hideLoading(url, null, false);
    }

    throttleMap.delete(response.config.url);

    if (Object.prototype.toString.call(response.data) === '[object Blob]') {
      let blobContent = await readBlob(response.data);
      try {
        response.data = JSON.parse(blobContent);
      } catch (error) {
        return response;
      }
    }

    // CHECK_SERVER 报错的时候放在外部处理
    if (
      response.data.statusCode === ResponseCommCodeEnum.SUCCESS ||
      response.data.statusCode === ResponseCommCodeEnum.CHECK_SERVER
    ) {
      return response.data;
    } else if (response.data.statusCode === ResponseCommCodeEnum.TOKEN_ERROR) {
      if (response.config?._retryCount ?? 0 >= maxRetries) {
        // Prevent infinite loop
        hideLoading(url, reqId, false); // Ensure loader hides
        return Promise.reject({ message: 'Max token retries reached' });
      }

      response.config._retryCount += 1;

      let recallResult = await checkTokenFn(response);
      if (!recallResult.success) {
        return Promise.reject(response.data);
      }
      return recallResult.response;
    } else if (response.data.statusCode === ResponseCommCodeEnum.USER_INACTIVE) {
      userInActive(false);
      alertMsg(i18n.t('ALERT_ACCOUNT_EXCEPTION_PLEASE_TRY_TO_LOGIN_AGAIN'), _.uniqueId('warning_'), 'warning');
      return Promise.reject(response.data);
    } else if (response.data.statusCode === ResponseCommCodeEnum.BED_VERSION) {
      return Promise.reject(response.data);
    } else if (errorCodeMapping.errorCode.includes(Number(response.data.statusCode))) {
      if (shouldHideError) {
        return Promise.reject(response.data);
      }
      errorMessageFun(response.data.statusCode);
      return Promise.reject(response.data);
    } else if (response.data.statusCode === ResponseCommCodeEnum.ADS_SERVICE_EXCEPTION) {
      alertMsg(response.data.statusMessage, _.uniqueId('warning_'), 'warning');
      return Promise.reject(response.data);
    } else if (response.data.statusCode === ResponseCommCodeEnum.DUTYASSIGN_UPLOADED_FAIL) {
      alertMsg(i18n.t('DUTY_ASSIGN_UPLOAD_DIALOG_UPLOADED_FAIL_HAVE_ERRORS_MESSAGE'), _.uniqueId('error_'));
      return Promise.reject(response.data);
    } else if (response.data.statusCode === ResponseCommCodeEnum.DUTYASSIGN_UPLOADED_ERROR) {
      alertMsg(i18n.t('DUTY_ASSIGN_UPLOAD_DIALOG_UPLOADED_ERROR_NO_RECORD_MESSAGE'), _.uniqueId('error_'));
      return Promise.reject(response.data);
    } else if (response.data.statusCode === ResponseCommCodeEnum.DUTYASSIGN_UPLOADED_NO_CORPID) {
      alertMsg(i18n.t('DUTY_ASSIGN_UPLOAD_DIALOG_UPLOADED_SUCCESS_NO_CORPID_MESSAGE'), _.uniqueId('error_'));
      return Promise.reject(response.data);
    } else if (response.data.statusCode === ResponseCommCodeEnum.EQUIPMENT_DUPLICATE_REQUEST) {
      alertMsg(i18n.t('EQUIPMENT_DUPLICATE_REQUEST_ERROR'), _.uniqueId('error_'));
      return Promise.reject(response.data);
    } else if (response.data.statusCode === ResponseCommCodeEnum.EHD_HA_CHAT_MESSAGE_STILL_SENDING) {
      alertMsg(i18n.t('EHANDOVER_HA_CHAT_MESSAGE_STILL_SENDING'), _.uniqueId('error_'));
      return Promise.reject(response.data);
    } else if (response.data.statusCode === ResponseCommCodeEnum.SCAN_HKID) {
      alertMsg(i18n.t('EQUIPMENT_SCAN_HKID_NOT_FOUND_ERROR'), _.uniqueId('error_'));
      return Promise.reject(response.data);
    } else if (response.data.statusCode === ResponseCommCodeEnum.EHANDOVER_UPLOAD_DOC_PAGE_EXCEEDED) {
      alertMsg(i18n.t('EHANDOVER_UPLOAD_DOC_PAGE_EXCEEDED'), _.uniqueId('error_'));
      return Promise.reject(response.data);
    } else {
      if (!response.config.url.includes('/corpId/verify')) {
        setTimeout(() => {
          alertMsg(response.data.statusMessage, _.uniqueId('error_'));
        }, 500);
      }
      return Promise.reject(response.data);
    }
  },
  (error) => {
    const {
      url,
      _requestId: reqId,
      _internalSkipLoading: shouldSkip,
      _isHideError: shouldHideError,
    } = error.config || {};

    // Cleanup throttle map on error too
    if (url) {
      throttleMap.delete(url); // Optional
    }

    // Hide loading using the specific ID
    if (url && !shouldSkip) {
      if (reqId) {
        hideLoading(url, reqId, false);
      } else {
        hideLoading(url, null, false);
      }
    }

    const isHandledByEhandover = ehandoverNetworkErrorControl.record(error);
    if (!isHandledByEhandover && !shouldHideError) {
      setTimeout(() => {
        alertMsg(i18n.t('ALERT_NETWORK_ERROR'), _.uniqueId('error_'));
      }, 300);
    }
    return Promise.reject(error);
  }
);

/**
 * Simple Http Client
 * @param {string} method
 * @param {string} url
 * @param {object} param
 * @param {object} AxiosConfig see https://github.com/axios/axios#request-config
 */
const httpClient = (method, url, params, AxiosConfig) => {
  let httpConfig = {};
  httpConfig = {
    method,
    url,
    params: method === 'get' || method === 'delete' ? params : null,
    data: method === 'post' ? params : null,
    ...AxiosConfig,
  };
  return new Promise((resolve, reject) => {
    instance(httpConfig)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const AxiosInstance = instance;

export default {
  /**
   * @param {string} url
   * @param {object} params optional
   * @param {object} AxiosConfig optional see https://github.com/axios/axios#request-config
   */
  post(url, params = null, AxiosConfig = null) {
    return httpClient('post', url, params, AxiosConfig);
  },

  /**
   * @param {string} url
   * @param {object} params optional
   * @param {object} AxiosConfig optional https://github.com/axios/axios#request-config
   */
  get(url, params = null, AxiosConfig = null) {
    return httpClient('get', url, params, AxiosConfig);
  },

  delete(url, params = null, AxiosConfig = null) {
    return httpClient('delete', url, params, AxiosConfig);
  },
};

export { getAuthorization };
